import { array, z } from "zod";
import { EProductType } from "../constants/product.constants";
import { RequestSource } from "../constants/session.constants";
import { PaymentStatusV4 } from "../interfaces/v4/subscription";
import {
  AdminOrderFulfillmentStatus,
  AdminOrderPaymentMode,
  AdminOrderStatus,
} from "./admin-order.schema";
import { recipientResponseSchema, userAddressResponseSchema } from "./user-address.schema";
import { productSingleVariantResponseSchema } from "../interfaces/schemas/product-v4.schemas";
import { discountResponseSchemaForOrder } from "../interfaces/discount";
import { orderPaymentResponseSchema } from "./order-payment.schema";
import { platformPetResponseSchema } from "./users.schema";
import {
  OrderLineItemFulfillmentStatus,
  OrderLineItemShippingStatus,
} from "./update-order-line-item.schema";
import { orderEditRequestResponseSchema } from "./admin-edit-request.schema";

export const orderAddressResponseSchema = userAddressResponseSchema
  .extend({
    recipient: recipientResponseSchema.optional(),
  })
  .passthrough();

export type OrderAddressResponseDto = z.infer<typeof orderAddressResponseSchema>;

/** Per-unit lifecycle status aggregated at the line-item group level. */
export enum OrderLineItemUnitStatus {
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
}

export const orderLineItemResponseSchema = z
  .object({
    id: z.string(),
    productId: z.string(),
    variantId: z.string(),
    pets: array(platformPetResponseSchema).nullish(),
    address: orderAddressResponseSchema.nullish(),
    quantity: z.number(),
    price: z.number().optional(),
    status: z.nativeEnum(OrderLineItemUnitStatus).nullish(),
    cancelledQuantity: z.number().nullish(),
    subscriptionId: z.string().nullish(),
    fulfillmentStatus: z.nativeEnum(OrderLineItemFulfillmentStatus).nullish(),
    shippingStatus: z.nativeEnum(OrderLineItemShippingStatus).nullish(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    productName: z.string().nullish(),
    productImage: z.string().nullish(),
    productType: z.nativeEnum(EProductType).nullish(),
    product: productSingleVariantResponseSchema.nullish(),
    createdAt: z.number(),
    updatedAt: z.number(),
    createdBy: z.string().nullish(),
    updatedBy: z.string().nullish(),
  })
  .passthrough();

export type OrderLineItemResponseDto = z.infer<typeof orderLineItemResponseSchema>;

export const orderCoinsResponseSchema = z.object({
  creditAmount: z.number(),
  debitAmount: z.number(),
  isCredited: z.boolean(),
  isDebitted: z.boolean(),
});

export type OrderCoinsResponseDto = z.infer<typeof orderCoinsResponseSchema>;

export const shippingResponseSchema = z.object({
  rate: z.number(),
  estimatedDeliveryDays: z.object({
    min: z.number(),
    max: z.number(),
  }),
  state: z.string(),
});

export type ShippingResponseDto = z.infer<typeof shippingResponseSchema>;

export const shopifyDetailsResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  discountId: z.string().optional(),
  orderId: z.string().optional(),
  invoiceUrl: z.string().optional(),
});

export type ShopifyDetailsResponseDto = z.infer<typeof shopifyDetailsResponseSchema>;

export const orderPaymentDetailsResponseSchema = z.object({
  provider: z.string(),
  status: z.nativeEnum(PaymentStatusV4),
  source: z.string(),
  juspay: z.object({
    orderId: z.string(),
    customOrderId: z.string(),
    customerId: z.string(),
    paymentLinks: z.object({
      web: z.string().optional(),
      mobile: z.string().optional(),
      iframe: z.string().optional(),
    }),
    merchantId: z.string(),
    clientAuthToken: z.object({
      expiry: z.string(),
      token: z.string(),
    }),
  }),
});

export type OrderPaymentDetailsResponseDto = z.infer<typeof orderPaymentDetailsResponseSchema>;

export const reviewOrderResponseSchema = z.object({
  rating: z.number().min(1).max(5),
  content: z.string().optional(),
  reviewedAt: z.number(),
});

export type ReviewOrderResponseDto = z.infer<typeof reviewOrderResponseSchema>;

export const orderDetailsResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  address: orderAddressResponseSchema.optional(),
  note: z.string().optional(),
  lineItems: z.array(orderLineItemResponseSchema),
  subTotal: z.number(),
  discount: discountResponseSchemaForOrder.optional(),
  coins: orderCoinsResponseSchema.optional(),
  total: z.number(),
  totalSavings: z.number().optional(),
  shipping: shippingResponseSchema.optional(),
  source: z.nativeEnum(RequestSource).optional(),
  shopify: shopifyDetailsResponseSchema.optional(),
  payments: z.array(orderPaymentResponseSchema).optional(),
  paymentMode: z.nativeEnum(AdminOrderPaymentMode),
  bookingFeeAmount: z.number().optional(),
  remainingAmount: z.number().optional(),
  fulfillmentStatus: z.nativeEnum(AdminOrderFulfillmentStatus).nullish(),
  status: z.nativeEnum(AdminOrderStatus),
  review: reviewOrderResponseSchema.optional(),
  createdBy: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  editRequests: z.array(orderEditRequestResponseSchema).nullish(),
});

export type OrderDetailsResponseDto = z.infer<typeof orderDetailsResponseSchema>;
